import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import SearchIcon from "@mui/icons-material/Search";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const formatDate = (value) => (value ? String(value).slice(0, 10) : "");
const emptyForm = {
  student_rollno: "",
  receipt_date: "",
  fee_amount: "",
  payment_mode: "",
  remarks: "",
  qrtno: "",
  trn_ref_no: "",
  trn_ref_date: "",
};

const exportColumns = [
  ["fee_receipt_id", "Receipt ID"],
  ["student_rollno", "Student No."],
  ["fee_head_name", "Fee Head"],
  ["receipt_date", "Receipt Date"],
  ["fee_amount", "Amount"],
  ["payment_mode", "Payment Mode"],
  ["qrtno", "QRT No."],
  ["trn_ref_no", "Transaction Ref."],
  ["remarks", "Remarks"],
];

export default function FeeReceipt() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const modifiedBy = user?.user_email || user?.username || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [paymentModes, setPaymentModes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const showMessage = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchRows = useCallback(
    async (searchValue) => {
      if (!clientId || !collegeId) return;
      const term = String(searchValue || "").trim();
      if (!term) {
        setRows([]);
        return showMessage("Enter Roll No., Receipt Number, or Date", "warning");
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({ search: term });
        const { data } = await axios.get(
          `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}?${params.toString()}`
        );

        setRows(
          (data?.data || []).map((row) => ({
            ...row,
            id: String(row.fee_receipt_id),
            fee_head_name: row.feeBook?.feeHead?.fee_head_name || "",
            student_rollno: row.feeBook?.student_rollno || "",
          }))
        );
      } catch (error) {
        showMessage(error?.response?.data?.message || "Unable to load Fee Receipts", "error");
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, clientId, collegeId]
  );

  useEffect(() => {
    setRows([]);
  }, [clientId, collegeId]);

  useEffect(() => {
    if (!clientId || !collegeId) return;
    axios
      .get(`${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/payment-modes`)
      .then(({ data }) => setPaymentModes(data?.data || []))
      .catch(() => setPaymentModes([]));
  }, [API_BASE_URL, clientId, collegeId]);

  const filteredRows = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        sno: index + 1,
      })),
    [rows]
  );

  const openEdit = (row) => {
    setEditing(row);
    setForm(
      Object.fromEntries(
        Object.keys(emptyForm).map((field) => [
          field,
          field.includes("date") ? formatDate(row[field]) : row[field] ?? "",
        ])
      )
    );
  };

  const saveEdit = async () => {
    if (!form.fee_amount || Number(form.fee_amount) <= 0)
      return showMessage("Enter a valid amount", "warning");
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/${editing.fee_receipt_id}`,
        { ...form, modified_by: modifiedBy }
      );
      showMessage(data?.message || "Fee Receipt updated successfully");
      setEditing(null);
      await fetchRows(search);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to update Fee Receipt", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteReceipt = async () => {
    setSaving(true);
    try {
      const { data } = await axios.delete(
        `${API_BASE_URL}/api/fee-receipts/${clientId}/${collegeId}/${deleting.fee_receipt_id}`
      );
      showMessage(data?.message || "Fee Receipt deleted successfully");
      setDeleting(null);
      await fetchRows(search);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to delete Fee Receipt", "error");
    } finally {
      setSaving(false);
    }
  };

  const getExportValue = (row, field) =>
    field.includes("date") ? formatDate(row[field]) : row[field] ?? "";

  const exportExcel = () => {
    const data = filteredRows.map((row) =>
      Object.fromEntries(exportColumns.map(([field, title]) => [title, getExportValue(row, field)]))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), "Fee Receipts");
    XLSX.writeFile(workbook, "Fee_Receipts.xlsx");
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.text("Fee Receipts", 14, 12);
    autoTable(doc, {
      startY: 18,
      head: [exportColumns.map(([, title]) => title)],
      body: filteredRows.map((row) => exportColumns.map(([field]) => getExportValue(row, field))),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [25, 118, 210] },
    });
    doc.save("Fee_Receipts.pdf");
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 80,
      },
      { field: "fee_receipt_id", headerName: "Receipt ID", width: 100 },
      { field: "student_rollno", headerName: "Student No.", width: 150 },
      { field: "fee_head_name", headerName: "Fee Head", minWidth: 390, flex: 1 },
      {
        field: "receipt_date",
        headerName: "Receipt Date",
        width: 120,
        valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString("en-GB") : ""),
      },
      { field: "fee_amount", headerName: "Amount", width: 110 },
      { field: "payment_mode", headerName: "Mode", width: 100 },
      { field: "qrtno", headerName: "QRT No.", width: 95 },
      { field: "trn_ref_no", headerName: "Tran Ref.", width: 145 },
      { field: "remarks", headerName: "Remarks", minWidth: 170, flex: 0.8 },
      {
        field: "actions",
        headerName: "Actions",
        width: 190,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="medium"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => openEdit(row)}
              sx={{ color: "#fff" }}
            >
              Edit
            </Button>
            <Button
              size="medium"
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleting(row)}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              Delete
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={1}>
          <TextField
            size="medium"
            label="Search Fee Receipts"
            placeholder="Roll No., Receipt No., or YYYY-MM-DD"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && fetchRows(search)}
            sx={{ width: { xs: "100%", sm: 340 } }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => fetchRows(search)}
            sx={{ color: "#fff" }}
          >
            Search
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            aria-label="Fee receipt download actions"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            disabled={!filteredRows.length}
            sx={{ color: "#fff" }}
          >
            Actions
          </Button>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} mb={1}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Fee Receipts ({filteredRows.length})
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            Total Amount:{" "}
            {filteredRows.reduce((sum, row) => sum + Number(row.fee_amount || 0), 0).toFixed(2)}
          </Typography>
        </Stack>
        <Box sx={{ height: 540, width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            pageSize={pageSize}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            pagination
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => {
              exportPdf();
              setAnchorEl(null);
            }}
            sx={{ backgroundColor: "#d32f2f", color: "#fff" }}
          >
            DOWNLOAD PDF
          </Button>
        </MenuItem>
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<TableViewIcon />}
            onClick={() => {
              exportExcel();
              setAnchorEl(null);
            }}
            sx={{ backgroundColor: "#4caf50", color: "#fff" }}
          >
            DOWNLOAD EXCEL
          </Button>
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Fee Receipt</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="primary" fontWeight={700} mb={1.5}>
            Receipt Details
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              size="medium"
              type="text"
              label="Roll No"
              disabled="true"
              value={form.student_rollno}
              onChange={(event) =>
                setForm((old) => ({ ...old, student_rollno: event.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="medium"
              type="date"
              label="Receipt Date"
              value={form.receipt_date}
              onChange={(event) => setForm((old) => ({ ...old, receipt_date: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              required
              size="medium"
              type="number"
              label="Amount"
              value={form.fee_amount}
              onChange={(event) => setForm((old) => ({ ...old, fee_amount: event.target.value }))}
              inputProps={{ min: 0.01, step: 0.01 }}
              InputProps={{ sx: { fontSize: "1.25rem", fontWeight: 700 } }}
            />
          </Box>

          <Typography variant="subtitle2" color="primary" fontWeight={700} mt={3} mb={1.5}>
            Payment Details
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              select
              size="medium"
              label="Payment Mode"
              value={form.payment_mode}
              onChange={(event) =>
                setForm((old) => ({
                  ...old,
                  payment_mode: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select Payment Mode</option>

              {[...new Set([...paymentModes, form.payment_mode].filter(Boolean))].map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="medium"
              type="number"
              label="QRT No."
              value={form.qrtno}
              onChange={(event) => setForm((old) => ({ ...old, qrtno: event.target.value }))}
            />
            <TextField
              fullWidth
              size="medium"
              label="Transaction Reference No."
              value={form.trn_ref_no}
              onChange={(event) => setForm((old) => ({ ...old, trn_ref_no: event.target.value }))}
            />
            <TextField
              fullWidth
              size="medium"
              type="date"
              label="Transaction Reference Date"
              value={form.trn_ref_date}
              onChange={(event) => setForm((old) => ({ ...old, trn_ref_date: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="medium"
              label="Remarks"
              value={form.remarks}
              onChange={(event) => setForm((old) => ({ ...old, remarks: event.target.value }))}
              sx={{ gridColumn: { sm: "1 / -1" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={saveEdit} sx={{ color: "#fff" }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Fee Receipt</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this receipt? Receipt No:{" "}
            <strong>{deleting?.fee_receipt_id}</strong>
          </Typography>
          <Typography>
            RollNo: <strong>{deleting?.student_rollno}</strong>
          </Typography>
          <Typography>
            Amount: <strong>{deleting?.fee_amount}</strong>
          </Typography>

          <Typography>Once deleted,This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={deleteReceipt}
            sx={{ backgroundColor: "#d32f2f", color: "#fff" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
